import React, { useState, useEffect } from 'react';
import { useAlert } from '../../../providers/AlertProvider';
import { downloadCSV as downloadGenericCSV } from '../../lib/functions';
import Button from '../Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

const TachoInfringementReport = ({ infringementData = [], theme }) => {
    const { showAlert } = useAlert();
    const [tableData, setTableData] = useState([]);

    const isLightTheme = theme === 'light';

    const displayColumns = [
        { key: 'infringement_date', label: 'Infringement Date' },
        { key: 'driver_name', label: 'Driver Name' },
        { key: 'infringement_type', label: 'Type' },
        { key: 'severity', label: 'Severity' },
        { key: 'description', label: 'Description' },
        { key: 'vehicle_registration', label: 'Vehicle Reg' },
        { key: 'status', label: 'Status' },
        { key: 'notes', label: 'Driver Notes' },
        { key: 'signed_at', label: 'Signed Date' },
        { key: 'created_at', label: 'Created Date' },
        { key: 'printout_required', label: 'Printout Required' },
    ];

    useEffect(() => {
        if (infringementData && infringementData.length > 0) {
            const formatted = infringementData.map((inf) => {
                // Format dates to dd/mm/yyyy
                const formatDate = (dateString) => {
                    if (!dateString) return 'N/A';
                    const dateObj = new Date(dateString);
                    return isNaN(dateObj) 
                        ? 'Invalid Date' 
                        : `${String(dateObj.getDate()).padStart(2, '0')}/${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;
                };

                return {
                    infringement_date: formatDate(inf.infringement_date),
                    driver_name: `${inf.driver_name || ''} ${inf.driver_surname || ''}`.trim() || 'Unknown Driver',
                    infringement_type: (inf.infringement_type || 'unknown').replace(/_/g, ' ').toUpperCase(),
                    severity: (inf.severity || 'unknown').replace(/_/g, ' ').toUpperCase(),
                    description: inf.description || 'No description',
                    vehicle_registration: inf.vehicle_registration || 'N/A',
                    status: (inf.status || 'unknown').replace(/_/g, ' ').toUpperCase(),
                    notes: inf.notes || '',
                    signed_at: formatDate(inf.signed_at),
                    created_at: formatDate(inf.created_at),
                    printout_required: inf.printout_required ? 'Yes' : 'No',
                };
            });

            setTableData(formatted);
        }
    }, [infringementData]);

    const downloadCSV = () => {
        if (tableData.length === 0) {
            showAlert({
                message: 'No data available to download.',
                type: 'warning',
                buttons: [{
                    type: 'info',
                    label: 'OK',
                }],
            });
            return;
        }

        try {
            // Format data for CSV with proper headers
            const csvData = tableData.map(row => ({
                'Infringement Date': row.infringement_date,
                'Driver Name': row.driver_name,
                'Infringement Type': row.infringement_type,
                'Severity': row.severity,
                'Description': row.description,
                'Vehicle Registration': row.vehicle_registration,
                'Status': row.status,
                'Driver Notes': row.notes,
                'Signed Date': row.signed_at,
                'Created Date': row.created_at,
                'Printout Required': row.printout_required,
            }));

            downloadGenericCSV(csvData);
            
            showAlert({
                message: `Successfully downloaded ${tableData.length} infringement records.`,
                type: 'success',
                buttons: [{
                    type: 'success',
                    label: 'OK',
                }],
            });
        } catch (error) {
            console.error('Error downloading CSV:', error);
            showAlert({
                message: 'An error occurred while downloading the report.',
                type: 'danger',
                buttons: [{
                    type: 'danger',
                    label: 'OK',
                }],
            });
        }
    };

    const sortByColumn = (column) => {
        const sorted = [...tableData].sort((a, b) => {
            const aVal = a[column]?.toString().toLowerCase() || '';
            const bVal = b[column]?.toString().toLowerCase() || '';
            return aVal.localeCompare(bVal);
        });
        setTableData(sorted);
    };

    const getSeverityColor = (severity) => {
        const sev = severity.toLowerCase();
        if (sev.includes('minor')) return isLightTheme ? '#ffa500' : '#ffb84d';
        if (sev.includes('serious')) return isLightTheme ? '#ff4500' : '#ff6347';
        if (sev.includes('very')) return isLightTheme ? '#dc143c' : '#ff0000';
        return isLightTheme ? '#666' : '#aaa';
    };

    const getStatusColor = (status) => {
        const stat = status.toLowerCase();
        if (stat.includes('signed')) return isLightTheme ? '#28a745' : '#4caf50';
        if (stat.includes('acknowledged')) return isLightTheme ? '#17a2b8' : '#00bcd4';
        if (stat.includes('pending')) return isLightTheme ? '#ffc107' : '#ffeb3b';
        return isLightTheme ? '#666' : '#aaa';
    };

    const containerStyle = {
        margin: '20px auto',
        padding: '20px',
        borderRadius: '10px',
        border: '2px solid',
        borderColor: isLightTheme ? '#ddd' : '#555',
        backgroundColor: isLightTheme ? '#fff' : '#2a2a2a',
        color: isLightTheme ? '#333' : '#fff',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px',
        fontSize: '14px',
    };

    const thStyle = {
        backgroundColor: isLightTheme ? '#f8f9fa' : '#1a1a1a',
        color: isLightTheme ? '#333' : '#fff',
        padding: '12px',
        textAlign: 'left',
        borderBottom: '2px solid ' + (isLightTheme ? '#ddd' : '#555'),
        cursor: 'pointer',
        fontWeight: 'bold',
    };

    const tdStyle = {
        padding: '12px',
        borderBottom: '1px solid ' + (isLightTheme ? '#ddd' : '#444'),
    };

    const buttonStyle = {
        padding: '12px 24px',
        backgroundColor: isLightTheme ? '#007bff' : '#0056b3',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
    };

    if (!infringementData || infringementData.length === 0) {
        return (
            <div style={containerStyle}>
                <h2 style={{ textAlign: 'center' }}>Tacho Infringement Report</h2>
                <p style={{ textAlign: 'center', color: isLightTheme ? '#666' : '#aaa' }}>
                    No infringement data available for the selected date range.
                </p>
            </div>
        );
    }

    return (
        <div style={containerStyle}>
            <h2 style={{ textAlign: 'center', marginBottom: '10px' }}>Tacho Infringement Report</h2>
            <p style={{ textAlign: 'center', color: isLightTheme ? '#666' : '#aaa', marginBottom: '20px' }}>
                Total Records: {tableData.length}
            </p>

            <Button onClick={downloadCSV} style={buttonStyle}>
                <FontAwesomeIcon icon={faDownload} style={{ width: 16 }} />
                Download CSV ({tableData.length} records)
            </Button>

            <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            {displayColumns.map((col) => (
                                <th
                                    key={col.key}
                                    style={thStyle}
                                    onClick={() => sortByColumn(col.key)}
                                    title="Click to sort"
                                >
                                    {col.label} ▾
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData.map((row, index) => (
                            <tr key={index} style={{
                                backgroundColor: index % 2 === 0 
                                    ? (isLightTheme ? '#f9f9f9' : '#2a2a2a')
                                    : (isLightTheme ? '#fff' : '#333')
                            }}>
                                <td style={tdStyle}>{row.infringement_date}</td>
                                <td style={tdStyle}>{row.driver_name}</td>
                                <td style={tdStyle}>{row.infringement_type}</td>
                                <td style={{...tdStyle, color: getSeverityColor(row.severity), fontWeight: 'bold'}}>
                                    {row.severity}
                                </td>
                                <td style={{...tdStyle, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {row.description}
                                </td>
                                <td style={tdStyle}>{row.vehicle_registration}</td>
                                <td style={{...tdStyle, color: getStatusColor(row.status), fontWeight: 'bold'}}>
                                    {row.status}
                                </td>
                                <td style={{...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                    {row.notes}
                                </td>
                                <td style={tdStyle}>{row.signed_at}</td>
                                <td style={tdStyle}>{row.created_at}</td>
                                <td style={tdStyle}>{row.printout_required}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TachoInfringementReport;
